/**
 * b2b-register.js — Antrag auf einen Haendlerzugang, Formular v2 (BA-50, 04.10.2026)
 * ---------------------------------------------------------------------------
 *
 * WAS PASSIERT HIER:
 * Das Formular aus sections/b2b-haendler.liquid wird nicht an Shopify
 * geschickt, sondern per fetch an den App-Proxy. Dort legt der Admin den
 * Antrag als `app_b2b_customer` mit Status `pending` an und alarmiert den
 * Owner; Kundenkonto, Tag `b2b` und Steuerbefreiung entstehen erst bei der
 * Freischaltung im Admin (Entscheidung D8).
 *
 * SPEZIFIKATION: ~/dev/blackoutsolar-admin/docs/B2B-HAENDLERSEITE-V2-2026-10.md
 * §D (Formular v2). Kurz:
 *   D.2  Reihenfolge: Land, Firma, UID (Praefix aus dem Land), optional
 *        „Ich habe keine UID-Nummer“ + Registernummer, Vorname, Nachname,
 *        E-Mail, Telefon, Strasse, Hausnummer, Adresszusatz, PLZ, Ort, Nachricht.
 *   D.3  UID: Praefix folgt dem Land, Pruefziffer als WARNUNG (nie ein Block).
 *   D.4  Pruefung beim Verlassen eines Felds und beim Absenden, nie je
 *        Tastendruck. Beim Absenden mit Fehlern: Banner mit Zaehlung, Fokus
 *        auf das erste fehlerhafte Feld. Autofill-Split „Hauptstraße 12“.
 *   D.5  Fehlertexte wortgleich mit dem Server (MSG unten).
 *   D.6  409 `duplicate` (anonym, Anmelde-Link) und `duplicate_own`
 *        (angemeldet, ohne Link): Wortlaut kommt vom Server.
 *   D.7  Erfolg v2a; `review` aus der Antwort waehlt spaeter die v2b-Anzeige
 *        (Polling auf den Status-Endpoint baut erst A2, hier nur der Platz).
 *
 * VERTRAG (Backend, BA-49 im Admin; Theme-Kopie dev/fixtures/b2b-proxy-contract.json):
 *   POST /apps/proxy/b2b/register
 *   Body  { countryCode, company, vatId, registerId, firstName,
 *           lastName, email, phone, streetName, houseNumber, address2, zip,
 *           city, note, ref, contactName, street, noVatId, consent, website,
 *           formStartedAt, leadId?, leadRef? }
 *         `contactName` (Vorname + Nachname) und `street` (Strasse + Nummer)
 *         schickt das Theme zusaetzlich kombiniert mit: ein Server, der die
 *         neuen Felder noch nicht kennt, nimmt den Antrag damit trotzdem an
 *         (Risiko R7 in beide Richtungen).
 *         `ref` bleibt der Partner-Empfehlungscode (Cookie bs_ref). `leadId` und
 *         `leadRef` sind die Anfrage-ID und der Vorgangscode aus denk-collect
 *         (window.denk.lead), nur dabei, wenn das SDK sie liefert (E2).
 *   201   { ok:true, id, review? }      review: 'manual' | 'checking' (F17)
 *   200   { ok:true }                    (Honeypot: stiller Erfolg)
 *   400   { ok:false, code:'validation', errors:{ feld:'Meldung', form:'…' } }
 *   409   duplicate (anonym, mit Anmelde-Link) · duplicate_own (angemeldet)
 *   429 rate_limited · 400 bad_request · 500 server_error, jeweils mit
 *         `message` (bereits in Sie-Form, mit Telefon und Mail)
 *
 * ANMELDE-LINK BEI 409 (BA-21): `duplicate` bittet um Anmeldung; der Banner
 * haengt einen echten Link an (actionLinkFor). Die URL kommt aus
 * `data-b2b-login-url` am Formular (snippets/login-url.liquid mit Rueckkehr
 * auf die Seite selbst, `page.url`), Rueckfall DEFAULT_LOGIN_URL.
 * `logged_in_customer_id` haengt Shopify selbst an die Proxy-Anfrage; von hier
 * geht NIE eine Kunden-ID raus.
 *
 * BOT-ABWEHR: `website` ist der Honeypot und wird IMMER mitgeschickt (leer).
 * `formStartedAt` setzt dieses Skript beim Laden der Seite, nicht Liquid —
 * eine im Full-Page-Cache eingebackene Zeit waere wertlos. Der Server verlangt
 * mindestens 3 Sekunden zwischen Aufbau und Absenden.
 *
 * SERVERTEXTE: jede Meldung des Servers landet ueber `textContent` im DOM,
 * nie ueber innerHTML.
 *
 * Klassisches Skript (kein Modul), laedt als `defer` und nur in dieser
 * Sektion. Die reinen Funktionen sind zusaetzlich ueber module.exports
 * erreichbar, damit assets/b2b-register.test.mjs sie ohne DOM testen kann
 * (`node --test assets/b2b-register.test.mjs`).
 */
(function (global, doc) {
  'use strict';

  // ────────────────────────────────────────────────────────────── Konstanten

  /** Fallback, wenn das Formular kein `data-endpoint` mitbringt. */
  var ENDPOINT = '/apps/proxy/b2b/register';

  /**
   * Rueckfall, falls das Formular keine `data-b2b-login-url` mitbringt:
   * Anmeldung der neuen Kundenkonten mit Rueckkehr auf /pages/b2b
   * (BA-37, BA-51, wie snippets/login-url.liquid und b2b-seite-url.liquid).
   */
  var DEFAULT_LOGIN_URL = '/customer_authentication/login?return_to=%2Fpages%2Fb2b';

  /**
   * Antwortcodes, nach denen der naechste Schritt die Anmeldung ist (Kategorie
   * `login` im Proxy-Vertrag): 409 `duplicate`. `duplicate_own` (angemeldet,
   * D.6) bekommt keinen Link: der Kunde ist ja schon angemeldet.
   */
  var LOGIN_CODES = ['duplicate'];

  /**
   * Textfelder des Formulars v2 in der Reihenfolge von D.2. Ein Drift-Waechter
   * in assets/b2b-register.test.mjs vergleicht sie mit `registrationSchema`
   * im Admin (src/server/services/b2b/registration.ts).
   */
  var FIELDS = [
    'countryCode',
    'company',
    'vatId',
    'registerId',
    'firstName',
    'lastName',
    'email',
    'phone',
    'streetName',
    'houseNumber',
    'address2',
    'zip',
    'city',
    'note',
    'ref'
  ];

  /**
   * Kombinierte Felder des Vertrags v1, die das Theme aus den v2-Feldern
   * ableitet (D.1 Nr. 3 und 4: der Server speichert sie ohnehin kombiniert).
   */
  var LEGACY_FIELDS = ['contactName', 'street'];

  /** Feldfehler des Servers zu einem v1-Feld landen am sichtbaren v2-Feld. */
  var ERROR_ALIASES = { contactName: 'firstName', street: 'streetName' };

  /** Kontrollkaestchen, Honeypot und Zeitstempel. */
  var NO_VAT_FIELD = 'noVatId';
  var CONSENT_FIELD = 'consent';
  var HONEYPOT_FIELD = 'website';
  var STARTED_FIELD = 'formStartedAt';
  var CHECKBOX_FIELDS = [NO_VAT_FIELD, CONSENT_FIELD];

  /**
   * Anfrage-Kennung aus denk-collect (E2): leadId (UUID) und Vorgangscode
   * (5 Zeichen). Optional, nur wenn das SDK mit Lead-API sie liefert; nicht in
   * PAYLOAD_KEYS, weil der Server dazu nie Feldfehler meldet.
   */
  var LEAD_FIELDS = ['leadId', 'leadRef'];
  var LEAD_ID_PATTERN = /^[0-9a-fA-F-]{32,36}$/;
  var LEAD_REF_PATTERN = /^[23456789ABCDEFGHJKMNPQRSTVWXYZ]{5}$/;

  /** Alles, was der Body traegt. Gleichzeitig die Allowlist fuer Feldfehler. */
  var PAYLOAD_KEYS = FIELDS.concat(LEGACY_FIELDS, [NO_VAT_FIELD, CONSENT_FIELD, HONEYPOT_FIELD, STARTED_FIELD]);

  /** Felder, die der Kunde sieht und die eine eigene Pruefung haben (Reihenfolge = Fokus-Reihenfolge). */
  var VISIBLE_FIELDS = [
    'countryCode',
    'company',
    'vatId',
    'registerId',
    'firstName',
    'lastName',
    'email',
    'phone',
    'streetName',
    'houseNumber',
    'address2',
    'zip',
    'city',
    'note',
    'consent'
  ];

  /** Schluessel fuer Fehler, die keinem Feld gehoeren (FORM_ERROR_KEY im Admin). */
  var FORM_ERROR_KEY = 'form';

  /**
   * Erlaubte Form eines Empfehlungscodes. MUSS identisch bleiben mit dem
   * Muster in snippets/referral-capture.liquid und assets/b2b-checkout.js.
   */
  var REF_PATTERN = /^[A-Za-z0-9_-]{2,64}$/;

  /** Cookie, in den snippets/referral-capture.liquid den Code schreibt. */
  var REF_COOKIE = 'bs_ref';

  /** Muster wie registration.ts (EMAIL_PATTERN, PHONE_PATTERN). */
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var PHONE_PATTERN = /^[+0-9][0-9\s()/.-]{4,29}$/;

  /** Registernummer (D.2 Nr. 3b): 4 bis 30 Zeichen. */
  var REGISTER_ID_PATTERN = /^[A-Za-z0-9 ./-]{4,30}$/;

  /** Laengen aus D.2 (Browser = Server). */
  var LIMITS = {
    company: [2, 200],
    registerId: [4, 30],
    firstName: [1, 60],
    lastName: [1, 60],
    email: [3, 254],
    phone: [0, 30],
    streetName: [2, 120],
    houseNumber: [1, 20],
    address2: [0, 120],
    city: [2, 120],
    note: [0, 2000]
  };

  /**
   * Kontaktweg (I25, 28.09.2026): Telefon UND Mail. Die Werte kommen aus den
   * Theme-Einstellungen (b2b_contact_phone / b2b_contact_email), die
   * sections/b2b-haendler.liquid als data-b2b-contact-phone/-email an das
   * Formular schreibt. Diese Werte sind nur der Rueckfall und entsprechen
   * B2B_CONTACT im Admin.
   */
  var DEFAULT_CONTACT = {
    phone: '+43 690 10122285',
    email: 'office@blackoutsolar.at'
  };

  function readContact(document) {
    var el = document && document.querySelector ? document.querySelector('[data-b2b-contact-phone]') : null;
    var phone = el ? el.getAttribute('data-b2b-contact-phone') : null;
    var email = el ? el.getAttribute('data-b2b-contact-email') : null;
    return {
      phone: (phone && phone.trim()) || DEFAULT_CONTACT.phone,
      email: (email && email.trim()) || DEFAULT_CONTACT.email
    };
  }

  /** Nur Pfade im eigenen Shop (`/...`, nicht `//fremder.host`), sonst der Rueckfall. */
  function ownPath(value, fallback) {
    if (typeof value !== 'string') return fallback;
    var url = value.trim();
    if (!url || url.charAt(0) !== '/' || url.charAt(1) === '/') return fallback;
    return url;
  }

  /** Ganzer Kontaktsatz, wortgleich mit contactSentence() im Admin. */
  function contactSentence(contact) {
    var c = contact || DEFAULT_CONTACT;
    return 'Rufen Sie uns an: ' + c.phone + ', oder schreiben Sie an ' + c.email + '.';
  }

  function buildCopy(contact) {
    return {
      loading: 'Antrag wird gesendet …',
      network:
        'Die Verbindung zu unserem Server ist unterbrochen. Bitte prüfen Sie Ihre Internetverbindung und senden Sie das Formular erneut. ' +
        contactSentence(contact),
      fallback:
        'Ihr Antrag konnte gerade nicht gesendet werden. Bitte versuchen Sie es in einigen Minuten noch einmal. ' +
        contactSentence(contact),
      login: 'Jetzt anmelden',
      emailFallback: 'Ihre E-Mail-Adresse'
    };
  }

  var COPY = buildCopy(readContact(doc));

  // ───────────────────────────────────────────────── Laender und Meldungen

  /**
   * Laendernamen wie im Admin (register.countries.names im Proxy-Vertrag,
   * VAT_COUNTRY_NAMES_DE in registration.ts) und wie in
   * snippets/b2b-country-options.liquid. Laender mit Artikel stehen in
   * COUNTRY_ARTICLES: Akkusativ nach „für“/„gewählt“ („Für die Niederlande“),
   * Dativ nach „in“ („in der Schweiz“).
   */
  var COUNTRY_NAMES = {
    AT: 'Österreich',
    BE: 'Belgien',
    BG: 'Bulgarien',
    CH: 'Schweiz',
    CZ: 'Tschechien',
    DE: 'Deutschland',
    DK: 'Dänemark',
    EE: 'Estland',
    ES: 'Spanien',
    FI: 'Finnland',
    FR: 'Frankreich',
    GR: 'Griechenland',
    HR: 'Kroatien',
    HU: 'Ungarn',
    IE: 'Irland',
    IT: 'Italien',
    LI: 'Liechtenstein',
    LT: 'Litauen',
    LU: 'Luxemburg',
    LV: 'Lettland',
    NL: 'Niederlande',
    PL: 'Polen',
    PT: 'Portugal',
    RO: 'Rumänien',
    SE: 'Schweden',
    SI: 'Slowenien',
    SK: 'Slowakei'
  };

  var COUNTRY_ARTICLES = {
    CH: { akk: 'die Schweiz', dat: 'der Schweiz' },
    NL: { akk: 'die Niederlande', dat: 'den Niederlanden' },
    SK: { akk: 'die Slowakei', dat: 'der Slowakei' }
  };

  /**
   * Fehlertexte D.5, Sie-Form, wortgleich mit dem Server (REGISTRATION_COPY in
   * registration.ts, BA-49; register.messages im Proxy-Vertrag). Gleiche
   * Schluessel wie dort. Platzhalter in geschweiften Klammern setzt
   * fillMessage() ein ({landAkk}, {landDat}, {muster}, {praefix}, {ziffern}).
   * Nur im Browser: vatFormatOk, consent, summaryOne/summaryMany.
   */
  var MSG = {
    countryMissing: 'Bitte wählen Sie das Land Ihres Unternehmens.',
    companyMissing: 'Bitte geben Sie Ihren Firmennamen an.',
    companyTooLong: 'Der Firmenname ist zu lang.',
    vatMissing: 'Bitte geben Sie Ihre UID-Nummer an.',
    vatMissingWithOption: 'Bitte geben Sie Ihre UID-Nummer an oder wählen Sie „Ich habe keine UID-Nummer“.',
    vatTooLong: 'Diese UID-Nummer ist zu lang.',
    vatFormat: 'Diese UID-Nummer hat kein gültiges Format. Für {landAkk} sieht sie so aus: {muster}.',
    vatCountry: 'Die UID-Nummer beginnt mit {praefix}, Sie haben aber {landAkk} gewählt. Bitte prüfen Sie Land oder Nummer.',
    vatChecksumWarning: 'Bitte prüfen Sie die Nummer, die letzte Ziffer passt nicht zu den übrigen.',
    exportVatId: 'Bitte geben Sie Ihre UID-Nummer nur mit Buchstaben und Ziffern an.',
    registerIdMissing: 'Bitte geben Sie Ihre GISA-Zahl, Firmenbuch- oder Registernummer an.',
    registerIdFormat:
      'Bitte geben Sie die Registernummer mit 4 bis 30 Zeichen an, nur Buchstaben, Ziffern, Leerzeichen, Punkt, Schrägstrich oder Bindestrich.',
    firstNameMissing: 'Bitte geben Sie Ihren Vornamen an.',
    firstNameTooLong: 'Der Vorname ist zu lang.',
    lastNameMissing: 'Bitte geben Sie Ihren Nachnamen an.',
    lastNameTooLong: 'Der Nachname ist zu lang.',
    emailInvalid: 'Bitte geben Sie eine gültige E-Mail-Adresse an, z. B. name@firma.at.',
    emailTooLong: 'Die E-Mail-Adresse ist zu lang.',
    phoneInvalid: 'Bitte geben Sie eine gültige Telefonnummer an, z. B. +43 660 1234567.',
    streetNameMissing: 'Bitte geben Sie die Straße an.',
    streetNameTooLong: 'Die Straße ist zu lang.',
    houseNumberMissing: 'Bitte geben Sie die Hausnummer an.',
    houseNumberInvalid: 'Bitte geben Sie die Hausnummer mit mindestens einer Ziffer an.',
    houseNumberTooLong: 'Die Hausnummer ist zu lang.',
    address2TooLong: 'Der Adresszusatz ist zu lang.',
    zipMissing: 'Bitte geben Sie die Postleitzahl an.',
    zipDigits: 'Die Postleitzahl hat in {landDat} {ziffern} Ziffern.',
    zipLiechtenstein: 'Postleitzahlen in Liechtenstein liegen zwischen 9485 und 9498.',
    zipOther: 'Bitte prüfen Sie die Postleitzahl.',
    cityMissing: 'Bitte geben Sie den Ort an.',
    cityTooLong: 'Der Ort ist zu lang.',
    noteTooLong: 'Ihre Nachricht ist zu lang.',
    vatFormatOk: 'Format in Ordnung',
    consent: 'Bitte bestätigen Sie, dass wir Ihre Angaben zur Bearbeitung Ihres Antrags verwenden dürfen.',
    summaryOne: 'Bitte prüfen Sie eine Angabe.',
    summaryMany: 'Bitte prüfen Sie {n} Angaben.'
  };

  /** Platzhalter `{name}` in einer Meldung ersetzen. */
  function fillMessage(template, values) {
    return String(template).replace(/\{([A-Za-z]+)\}/g, function (all, key) {
      return values && Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : all;
    });
  }

  /** Laendername ohne Artikel („Schweiz“). */
  function countryName(code) {
    var c = String(code || '').toUpperCase();
    return Object.prototype.hasOwnProperty.call(COUNTRY_NAMES, c) ? COUNTRY_NAMES[c] : c;
  }

  /** „Österreich“, „die Niederlande“ (nach „für“, „gewählt“). */
  function countryAkk(code) {
    var c = String(code || '').toUpperCase();
    return Object.prototype.hasOwnProperty.call(COUNTRY_ARTICLES, c) ? COUNTRY_ARTICLES[c].akk : countryName(c);
  }

  /** „Österreich“, „der Schweiz“ (nach „in“). */
  function countryDat(code) {
    var c = String(code || '').toUpperCase();
    return Object.prototype.hasOwnProperty.call(COUNTRY_ARTICLES, c) ? COUNTRY_ARTICLES[c].dat : countryName(c);
  }

  // ───────────────────────────────────────── UID-Pruefung im Browser (BA-22)

  /*
   * Die QUELLE bleibt der Server: Muster, Normalisierung und Regeln sind Kopien
   * aus dem Admin-Repo, src/server/services/b2b/vatFormat.ts (VAT_PATTERNS,
   * normalizeVatId, parseVatId, vatPrefixToIsoCountry, EU_VAT_PREFIXES),
   * vatChecksum.ts (checkVatChecksum, BA-50) und registration.ts
   * (validateVatId, B2B_REGISTRATION_COUNTRIES, B2B_REGISTRATION_EXCLUDED,
   * normalizeExportVatId, EXPORT_VAT_ID_MESSAGE). Drift-Waechter in
   * assets/b2b-register.test.mjs (Admin-Pfad per B2B_ADMIN_REPO waehlbar).
   * Die Browser-Pruefung darf NIE strenger sein als der Server.
   */

  /** Formatmuster je UID-Praefix, 1:1 aus vatFormat.ts (VAT_PATTERNS). */
  var VAT_PATTERNS = {
    AT: /^ATU\d{8}$/,
    BE: /^BE[01]\d{9}$/,
    BG: /^BG\d{9,10}$/,
    CY: /^CY\d{8}[A-Z]$/,
    CZ: /^CZ\d{8,10}$/,
    DE: /^DE\d{9}$/,
    DK: /^DK\d{8}$/,
    EE: /^EE\d{9}$/,
    EL: /^EL\d{9}$/,
    ES: /^ES[A-Z0-9]\d{7}[A-Z0-9]$/,
    FI: /^FI\d{8}$/,
    FR: /^FR[A-Z0-9]{2}\d{9}$/,
    HR: /^HR\d{11}$/,
    HU: /^HU\d{8}$/,
    IE: /^IE(\d{7}[A-W]|[7-9][A-Z*+]\d{5}[A-W]|\d{7}[A-W][A-I])$/,
    IT: /^IT\d{11}$/,
    LT: /^LT(\d{9}|\d{12})$/,
    LU: /^LU\d{8}$/,
    LV: /^LV\d{11}$/,
    MT: /^MT\d{8}$/,
    NL: /^NL\d{9}B\d{2}$/,
    PL: /^PL\d{10}$/,
    PT: /^PT\d{9}$/,
    RO: /^RO\d{2,10}$/,
    SE: /^SE\d{10}01$/,
    SI: /^SI\d{8}$/,
    SK: /^SK\d{10}$/,
    XI: /^XI(\d{9}|\d{12}|GD\d{3}|HA\d{3})$/
  };

  /**
   * Beispiel-UID je PRAEFIX fuer Platzhalter und Formatmeldung (D.2 Nr. 3,
   * D.5), wie VAT_ID_EXAMPLES im Admin (register.vatExamples im Vertrag).
   * GR steht unter EL. Jedes Beispiel passt zum Muster seines Praefixes (Test).
   */
  var VAT_EXAMPLES = {
    AT: 'ATU12345678',
    BE: 'BE0123456789',
    BG: 'BG123456789',
    CZ: 'CZ12345678',
    DE: 'DE123456789',
    DK: 'DK12345678',
    EE: 'EE123456789',
    EL: 'EL123456789',
    ES: 'ESX1234567X',
    FI: 'FI12345678',
    FR: 'FRXX123456789',
    HR: 'HR12345678901',
    HU: 'HU12345678',
    IE: 'IE1234567T',
    IT: 'IT12345678901',
    LT: 'LT123456789',
    LU: 'LU12345678',
    LV: 'LV12345678901',
    NL: 'NL123456789B01',
    PL: 'PL1234567890',
    PT: 'PT123456789',
    RO: 'RO1234567890',
    SE: 'SE123456789001',
    SI: 'SI12345678',
    SK: 'SK1234567890'
  };

  /** Beispiel fuer ein Land (ISO-Code, GR → EL), leer ohne Eintrag. */
  function vatExampleFor(countryCode) {
    var c = upperCountry(countryCode);
    var prefix = c === 'GR' ? 'EL' : c;
    return Object.prototype.hasOwnProperty.call(VAT_EXAMPLES, prefix) ? VAT_EXAMPLES[prefix] : '';
  }

  /** Platzhalter des UID-Felds in CH/LI (freiwillig, freier Text). */
  var EXPORT_VAT_EXAMPLE = 'CHE-123.456.789 MWST';

  /** EU-Mitgliedstaaten als ISO-Codes (UID-Praefix EL ist ISO GR). UID-Pflicht. */
  var EU_ISO_COUNTRIES = [
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'GR', 'ES',
    'FI', 'FR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT',
    'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'
  ];

  /** UID-Praefixe der EU (EU_VAT_PREFIXES in vatFormat.ts: GR heisst EL). */
  var EU_VAT_PREFIXES = EU_ISO_COUNTRIES.map(function (code) {
    return code === 'GR' ? 'EL' : code;
  });

  /**
   * EU-Laender ohne Registrierung (B2B_REGISTRATION_EXCLUDED, BA-23): die
   * Haendlerkasse liefert nicht nach Zypern und Malta.
   */
  var REGISTRATION_EXCLUDED = ['CY', 'MT'];

  /** Ausfuhrlaender (B2B_EXPORT_COUNTRIES): UID freiwillig, freier Text (BA-23). */
  var EXPORT_COUNTRIES = ['CH', 'LI'];

  /** Laender, fuer die der Server die UID prueft (B2B_REGISTRATION_COUNTRIES). */
  var VAT_CHECKED_COUNTRIES = EU_ISO_COUNTRIES.filter(function (code) {
    return !contains(REGISTRATION_EXCLUDED, code);
  }).concat(EXPORT_COUNTRIES);

  /** CH/LI-UID nach der Normalisierung: hoechstens 20 Zeichen, A bis Z und 0 bis 9. */
  var EXPORT_VAT_ID_PATTERN = /^[A-Z0-9]{1,20}$/;

  /**
   * Beschriftung des UID-Felds je Land (BA-23). Ohne JavaScript steht die
   * neutrale Beschriftung aus sections/b2b-haendler.liquid (= eu).
   */
  var VAT_LABELS = {
    eu: 'UID-Nummer',
    export: 'UID oder MWST-Nummer (freiwillig)'
  };

  function contains(list, value) {
    for (var i = 0; i < list.length; i++) if (list[i] === value) return true;
    return false;
  }

  /** Wert als getrimmter String oder null (leere Werte gelten als nicht gesetzt). */
  function asText(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number' && isFinite(value)) value = String(value);
    if (typeof value !== 'string') return null;
    var trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  function upperCountry(countryCode) {
    var c = asText(countryCode);
    return c ? c.toUpperCase() : '';
  }

  /** Wie normalizeVatId(): Trenner raus, gross, GR vorne wird EL. */
  function normalizeVatId(raw) {
    if (typeof raw !== 'string' || raw.length === 0) return '';
    var cleaned = raw.replace(/[\s.\-\/_]/g, '').toUpperCase();
    if (cleaned.slice(0, 2) === 'GR') return 'EL' + cleaned.slice(2);
    return cleaned;
  }

  /** Wie normalizeExportVatId(): trimmen, gross, Leerzeichen/Punkte/Bindestriche raus. */
  function normalizeExportVatId(raw) {
    if (typeof raw !== 'string') return '';
    return raw.trim().toUpperCase().replace(/[\s.-]/g, '');
  }

  function isExportCountry(countryCode) {
    return contains(EXPORT_COUNTRIES, upperCountry(countryCode));
  }

  function isEuCountry(countryCode) {
    return contains(EU_ISO_COUNTRIES, upperCountry(countryCode));
  }

  /** Beschriftung des UID-Felds fuer ein Land: CH/LI freiwillig, sonst neutral. */
  function vatLabelFor(countryCode) {
    return isExportCountry(countryCode) ? VAT_LABELS.export : VAT_LABELS.eu;
  }

  /** Wie parseVatId(): { prefix, number } oder null. */
  function parseVatId(raw) {
    var match = /^([A-Z]{2})([A-Z0-9+*]{2,14})$/.exec(normalizeVatId(raw));
    return match ? { prefix: match[1], number: match[2] } : null;
  }

  /** Wie vatPrefixToIsoCountry(): nur EL weicht ab (GR). */
  function vatPrefixToIsoCountry(prefix) {
    var upper = String(prefix || '').toUpperCase();
    return upper === 'EL' ? 'GR' : upper;
  }

  /** Wie isEuVatFormatValid(): Format passt zum Muster des Praefixes. */
  function isVatFormatValid(raw) {
    var parsed = parseVatId(raw);
    if (!parsed) return false;
    var pattern = Object.prototype.hasOwnProperty.call(VAT_PATTERNS, parsed.prefix) ? VAT_PATTERNS[parsed.prefix] : null;
    return pattern ? pattern.test(parsed.prefix + parsed.number) : false;
  }

  // ─────────────────────────────── D.3 Praefix aus dem Land, Platzhalter, Hilfe

  /** Vorbelegtes Praefix je Land: AT → ATU, GR → EL, CH/LI leer, sonst ISO. */
  function vatPrefixFor(countryCode) {
    var c = upperCountry(countryCode);
    if (!c || isExportCountry(c)) return '';
    if (c === 'AT') return 'ATU';
    if (c === 'GR') return 'EL';
    return isEuCountry(c) ? c : '';
  }

  /**
   * Steht im Feld nur ein Praefix (oder gar nichts)? Dann gilt es als leer:
   * beim Laenderwechsel wird es ersetzt, beim Absenden als „fehlt“ gemeldet
   * und leer gesendet.
   */
  function isBareVatPrefix(value) {
    var v = normalizeVatId(typeof value === 'string' ? value : '');
    if (v === '') return true;
    if (v === 'ATU' || v === 'AT' || v === 'EL') return true;
    return v.length === 2 && isEuCountry(v);
  }

  /**
   * Neuer Wert des UID-Felds nach einem Laenderwechsel (D.3): leeres Feld oder
   * blosses altes Praefix wird durch das neue Praefix ersetzt; eine echte
   * Nummer bleibt stehen (der Laendercheck meldet sich dann).
   */
  function nextVatValue(current, countryCode) {
    if (isBareVatPrefix(current)) return vatPrefixFor(countryCode);
    return typeof current === 'string' ? current : '';
  }

  /** Platzhalter des UID-Felds je Land. */
  function vatPlaceholderFor(countryCode) {
    return isExportCountry(countryCode) ? EXPORT_VAT_EXAMPLE : vatExampleFor(countryCode);
  }

  /** Hilfetext unter dem UID-Feld je Land (D.2 Nr. 3). */
  function vatHintFor(countryCode) {
    var c = upperCountry(countryCode);
    if (isExportCountry(c)) return 'Freiwillig, z. B. ' + EXPORT_VAT_EXAMPLE + '.';
    if (c === 'AT') return 'Steht auf Ihren Rechnungen, beginnt mit ATU.';
    if (c === 'DE') return 'In Deutschland: USt-IdNr., beginnt mit DE.';
    var prefix = vatPrefixFor(c);
    return prefix ? 'Beginnt mit ' + prefix + '.' : '';
  }

  /** PLZ-Tastatur: Ziffernblock fuer AT, DE, CH, LI, sonst Text. */
  function zipInputModeFor(countryCode) {
    return contains(['AT', 'DE', 'CH', 'LI'], upperCountry(countryCode)) ? 'numeric' : 'text';
  }

  /** PLZ-Platzhalter je Land (D.2 Nr. 11). */
  function zipPlaceholderFor(countryCode) {
    var c = upperCountry(countryCode);
    if (c === 'DE') return '12345';
    if (c === 'LI') return '9490';
    if (c === 'AT' || c === 'CH') return '1234';
    return '';
  }

  // ─────────────────────────────── Pruefziffern-Port (vatChecksum.ts, BA-50)

  /*
   * 1:1-Port von checkVatChecksum() aus dem Admin-Repo
   * (src/server/services/b2b/vatChecksum.ts). Ergebnis:
   *   'ok'    Pruefziffer stimmt
   *   'fail'  Pruefziffer stimmt nicht (Tippfehler, Zahlendreher)
   *   'none'  fuer dieses Land/Format keine Pruefziffer hinterlegt
   * `fail` ist ein HINWEIS, kein Urteil (das spricht VIES), deshalb zeigt das
   * Formular nur eine Warnung und sendet trotzdem. Der Drift-Test in
   * assets/b2b-register.test.mjs laesst Admin-Datei und Port ueber viele
   * Nummern laufen und vergleicht jedes Ergebnis.
   * Abweichung im Port: NL rechnet MOD 97 ziffernweise statt mit BigInt
   * (gleiches Ergebnis, laeuft auch in alten Browsern).
   */

  function digitsOf(value) {
    var out = [];
    for (var i = 0; i < value.length; i++) out.push(Number(value.charAt(i)));
    return out;
  }

  function weightedSum(digits, weights) {
    var sum = 0;
    for (var i = 0; i < weights.length; i++) sum += (digits[i] || 0) * weights[i];
    return sum;
  }

  /** Luhn ueber die gesamte Ziffernfolge (letzte Ziffer = Pruefziffer). */
  function luhnValid(value) {
    var sum = 0;
    var digits = digitsOf(value).reverse();
    for (var i = 0; i < digits.length; i++) {
      var d = digits[i];
      if (i % 2 === 1) {
        d *= 2;
        if (d > 9) d -= 9;
      }
      sum += d;
    }
    return sum % 10 === 0;
  }

  function okIf(condition) {
    return condition ? 'ok' : 'fail';
  }

  function checkAT(n) {
    var m = /^U(\d{8})$/.exec(n);
    if (!m) return 'fail';
    var d = digitsOf(m[1]);
    var sum = 0;
    for (var i = 0; i < 7; i++) {
      var p = d[i] * (i % 2 === 0 ? 1 : 2);
      sum += p > 9 ? Math.floor(p / 10) + (p % 10) : p;
    }
    return okIf((10 - ((sum + 4) % 10)) % 10 === d[7]);
  }

  /** ISO 7064 MOD 11,10. */
  function checkDE(n) {
    if (!/^\d{9}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var product = 10;
    for (var i = 0; i < 8; i++) {
      var sum = (d[i] + product) % 10;
      if (sum === 0) sum = 10;
      product = (2 * sum) % 11;
    }
    var check = 11 - product;
    if (check === 10) check = 0;
    return okIf(check === d[8]);
  }

  function checkBE(n) {
    if (!/^\d{10}$/.test(n)) return 'fail';
    return okIf(97 - (Number(n.slice(0, 8)) % 97) === Number(n.slice(8)));
  }

  function checkDK(n) {
    if (!/^\d{8}$/.test(n)) return 'fail';
    return okIf(weightedSum(digitsOf(n), [2, 7, 6, 5, 4, 3, 2, 1]) % 11 === 0);
  }

  function checkEE(n) {
    if (!/^\d{9}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var sum = weightedSum(d, [3, 7, 1, 3, 7, 1, 3, 7]);
    return okIf((10 - (sum % 10)) % 10 === d[8]);
  }

  function checkEL(n) {
    if (!/^\d{9}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var sum = 0;
    for (var i = 0; i < 8; i++) sum += d[i] * Math.pow(2, 8 - i);
    return okIf((sum % 11) % 10 === d[8]);
  }

  function checkFI(n) {
    if (!/^\d{8}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var r = weightedSum(d, [7, 9, 10, 5, 8, 4, 2]) % 11;
    if (r === 1) return 'fail';
    return okIf((r === 0 ? 0 : 11 - r) === d[7]);
  }

  function checkFR(n) {
    var m = /^([A-Z0-9]{2})(\d{9})$/.exec(n);
    if (!m) return 'fail';
    var key = m[1];
    // Neuer Schluessel mit Buchstaben: kein oeffentlich belastbares Verfahren.
    if (!/^\d{2}$/.test(key)) return 'none';
    return okIf((12 + 3 * (Number(m[2]) % 97)) % 97 === Number(key));
  }

  function checkHU(n) {
    if (!/^\d{8}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var sum = weightedSum(d, [9, 7, 3, 1, 9, 7, 3]);
    return okIf((10 - (sum % 10)) % 10 === d[7]);
  }

  var IE_CHECK_LETTERS = 'WABCDEFGHIJKLMNOPQRSTUV';

  function checkIE(n) {
    var m = /^(\d{7})([A-W])([A-I])?$/.exec(n);
    // Altformat (Ziffer, Buchstabe/+/*, 5 Ziffern, Buchstabe): ohne Pruefung.
    if (!m) return 'none';
    var d = digitsOf(m[1]);
    var sum = weightedSum(d, [8, 7, 6, 5, 4, 3, 2]);
    if (m[3]) sum += (m[3].charCodeAt(0) - 64) * 9;
    return okIf(IE_CHECK_LETTERS.charAt(sum % 23) === m[2]);
  }

  function checkIT(n) {
    if (!/^\d{11}$/.test(n)) return 'fail';
    return okIf(luhnValid(n));
  }

  function checkLT(n) {
    if (!/^(\d{9}|\d{12})$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var len = d.length - 1;
    var first = [];
    var second = [];
    for (var i = 0; i < len; i++) {
      first.push((i % 9) + 1);
      second.push(((i + 2) % 9) + 1);
    }
    var r = weightedSum(d, first) % 11;
    if (r === 10) {
      r = weightedSum(d, second) % 11;
      if (r === 10) r = 0;
    }
    return okIf(r === d[len]);
  }

  function checkLU(n) {
    if (!/^\d{8}$/.test(n)) return 'fail';
    return okIf(Number(n.slice(0, 6)) % 89 === Number(n.slice(6)));
  }

  /** Rest einer Ziffernfolge beliebiger Laenge modulo m (ohne BigInt). */
  function modDigits(digits, m) {
    var r = 0;
    for (var i = 0; i < digits.length; i++) r = (r * 10 + Number(digits.charAt(i))) % m;
    return r;
  }

  /** NL: altes Elfproef ODER (seit 2020 fuer Einzelunternehmer) ISO 7064 MOD 97-10. */
  function checkNL(n) {
    var m = /^(\d{9})B(\d{2})$/.exec(n);
    if (!m) return 'fail';
    var d = digitsOf(m[1]);
    var legacy = weightedSum(d, [9, 8, 7, 6, 5, 4, 3, 2]) % 11;
    if (legacy !== 10 && legacy === d[8]) return 'ok';
    var numeric = ('NL' + n).replace(/[A-Z]/g, function (c) {
      return String(c.charCodeAt(0) - 55);
    });
    return okIf(modDigits(numeric, 97) === 1);
  }

  function checkPL(n) {
    if (!/^\d{10}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var r = weightedSum(d, [6, 5, 7, 2, 3, 4, 5, 6, 7]) % 11;
    return okIf(r !== 10 && r === d[9]);
  }

  function checkPT(n) {
    if (!/^\d{9}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var check = 11 - (weightedSum(d, [9, 8, 7, 6, 5, 4, 3, 2]) % 11);
    if (check > 9) check = 0;
    return okIf(check === d[8]);
  }

  function checkSE(n) {
    if (!/^\d{10}01$/.test(n)) return 'fail';
    return okIf(luhnValid(n.slice(0, 10)));
  }

  function checkSI(n) {
    if (!/^[1-9]\d{7}$/.test(n)) return 'fail';
    var d = digitsOf(n);
    var check = 11 - (weightedSum(d, [8, 7, 6, 5, 4, 3, 2]) % 11);
    if (check === 11) return 'fail';
    if (check === 10) check = 0;
    return okIf(check === d[7]);
  }

  function checkSK(n) {
    if (!/^\d{10}$/.test(n)) return 'fail';
    return okIf(modDigits(n, 11) === 0);
  }

  var CHECKSUM_CHECKERS = {
    AT: checkAT,
    BE: checkBE,
    DE: checkDE,
    DK: checkDK,
    EE: checkEE,
    EL: checkEL,
    FI: checkFI,
    FR: checkFR,
    HU: checkHU,
    IE: checkIE,
    IT: checkIT,
    LT: checkLT,
    LU: checkLU,
    NL: checkNL,
    PL: checkPL,
    PT: checkPT,
    SE: checkSE,
    SI: checkSI,
    SK: checkSK
  };

  /**
   * Pruefziffer einer UID wie checkVatChecksum() im Admin. Nicht zerlegbare
   * UIDs ergeben 'fail', Laender ohne hinterlegtes Verfahren 'none'.
   */
  function checkVatChecksum(vatId) {
    var parsed = parseVatId(typeof vatId === 'string' ? vatId : '');
    if (!parsed) return 'fail';
    var checker = Object.prototype.hasOwnProperty.call(CHECKSUM_CHECKERS, parsed.prefix) ? CHECKSUM_CHECKERS[parsed.prefix] : null;
    if (!checker) return 'none';
    return checker(parsed.number);
  }

  // ─────────────────────────────────────────────────── Feldregeln (D.2, D.5)

  /**
   * UID-Regel des Servers (validateVatId in registration.ts, BA-49) fuer ein
   * Land. Meldung oder null.
   *   EU-Land: Pflicht (ausser „keine UID“), hoechstens 30 Zeichen, erst das
   *     Land (getipptes EU-Praefix passt nicht), dann das Format.
   *   CH/LI (BA-23): freiwillig, freier Text, hoechstens 20 Zeichen A bis Z, 0 bis 9.
   *   Anderes Land (auch CY/MT): keine Pruefung hier, das meldet der Server am Land.
   * `options.allowNoVat`: das Formular bietet „Ich habe keine UID-Nummer“ an
   * (Section-Setting ohne_uid_erlauben = Server-Schalter); dann nennt die
   * Pflichtmeldung den Ausweg.
   */
  function validateVatId(vatId, countryCode, options) {
    var opts = options || {};
    var country = upperCountry(countryCode);
    if (!contains(VAT_CHECKED_COUNTRIES, country)) return null;

    if (contains(EXPORT_COUNTRIES, country)) {
      var text = normalizeExportVatId(asText(vatId) || '');
      if (!text) return null;
      return EXPORT_VAT_ID_PATTERN.test(text) ? null : MSG.exportVatId;
    }

    var value = asText(vatId);
    if (!value || isBareVatPrefix(value)) {
      return opts.allowNoVat ? MSG.vatMissingWithOption : MSG.vatMissing;
    }
    if (value.length > 30) return MSG.vatTooLong;

    var normalized = normalizeVatId(value);
    var expected = country === 'GR' ? 'EL' : country;
    var typed = normalized.slice(0, 2);
    if (/^[A-Z]{2}$/.test(typed) && typed !== expected && contains(EU_VAT_PREFIXES, typed)) {
      return fillMessage(MSG.vatCountry, { praefix: typed, landAkk: countryAkk(country) });
    }
    var parsed = parseVatId(normalized);
    if (!isVatFormatValid(normalized) || !parsed) return vatFormatMessage(country);
    if (vatPrefixToIsoCountry(parsed.prefix) !== country) {
      return fillMessage(MSG.vatCountry, { praefix: parsed.prefix, landAkk: countryAkk(country) });
    }
    return null;
  }

  /** Formatmeldung D.5 mit Land und Muster (ohne Muster der Rueckfall wie im Admin). */
  function vatFormatMessage(country) {
    var example = vatExampleFor(country);
    return example
      ? fillMessage(MSG.vatFormat, { landAkk: countryAkk(country), muster: example })
      : 'Diese UID-Nummer hat kein gültiges Format. Bitte prüfen Sie Ihre Eingabe.';
  }

  /**
   * Zusatzhinweis zum UID-Feld (D.3), nur wenn die Nummer sonst in Ordnung ist:
   *   { kind:'warning', message } Pruefziffer passt nicht (kein Block)
   *   { kind:'ok', message }      Format in Ordnung (nie „gültig“, das sagt nur VIES)
   *   null                        nichts anzeigen
   */
  function vatNote(vatId, countryCode) {
    var country = upperCountry(countryCode);
    if (!isEuCountry(country) || contains(REGISTRATION_EXCLUDED, country)) return null;
    var value = asText(vatId);
    if (!value || isBareVatPrefix(value)) return null;
    if (validateVatId(value, country)) return null;
    var state = checkVatChecksum(value);
    if (state === 'fail') return { kind: 'warning', message: MSG.vatChecksumWarning };
    return { kind: 'ok', message: MSG.vatFormatOk };
  }

  /** PLZ je Land (D.2 Nr. 11), wie validateZip() im Admin. Meldung oder null. */
  function validateZip(zip, countryCode) {
    var country = upperCountry(countryCode);
    var value = asText(zip) || '';
    if (value === '') return MSG.zipMissing;
    if (country === 'AT' || country === 'CH' || country === 'LI') {
      if (!/^\d{4}$/.test(value)) return fillMessage(MSG.zipDigits, { landDat: countryDat(country), ziffern: 4 });
      if (country === 'LI') {
        var n = Number(value);
        if (n < 9485 || n > 9498) return MSG.zipLiechtenstein;
      }
      return null;
    }
    if (country === 'DE') {
      return /^\d{5}$/.test(value) ? null : fillMessage(MSG.zipDigits, { landDat: countryDat(country), ziffern: 5 });
    }
    return value.length >= 3 && value.length <= 10 ? null : MSG.zipOther;
  }

  function length(value) {
    return (asText(value) || '').length;
  }

  /**
   * Autofill-Split (D.4): „Hauptstraße 12“ im Strassenfeld bei leerer
   * Hausnummer → { street:'Hauptstraße', number:'12' }, sonst null.
   */
  // Muster aus D.4, die erste Gruppe aber genuegsam (`.*?`), damit
  // „Am Platz 3 - 5“ die ganze Nummer „3-5“ bekommt. Steht Stiege/Top/Tuer im
  // Strassenteil, bleibt alles unangetastet (das gehoert in den Adresszusatz).
  var STREET_SPLIT = /^(.*?\D)\s+(\d+[a-zA-Z]?(?:\s*[/-]\s*\d+[a-zA-Z]?)*)$/;
  var STREET_SPLIT_STOP = /\b(top|stiege|stg|tür|tuer)\b/i;

  function splitStreet(value) {
    var text = asText(value);
    if (!text) return null;
    var m = STREET_SPLIT.exec(text);
    if (!m || STREET_SPLIT_STOP.test(m[1])) return null;
    var street = m[1].replace(/[\s,]+$/, '');
    if (!street) return null;
    return { street: street, number: m[2].replace(/\s+/g, '') };
  }

  /**
   * BA-54 (05.10.2026): Strasse und Hausnummer abgleichen, auch nach Autofill.
   * iPhone/iCloud-Schluesselbund fuellt „Hauptstraße 12“ ins Strassenfeld UND
   * „12“ in die Hausnummer, ohne dass ein blur kommt. Ergebnis: neue Werte
   * { street, number } oder null (nichts aendern).
   *
   *   (a) Strasse endet nicht mit einer Nummer, oder Stiege/Top/Stg./Tuer im
   *       Strassenteil (bestehende Regel aus splitStreet)  → null
   *   (b) Hausnummer = Nummer am Ende der Strasse (Leerzeichen und
   *       Gross-/Kleinschreibung egal) → Nummer aus der Strasse entfernen
   *   (c) Hausnummer leer → wie bisher splitten
   *   (d) Hausnummer = der ganze Strassentext (Autofill hat address-line1 in
   *       beide Felder gelegt) → splitten
   *   sonst (andere Nummer von Hand) → null, der Kunde entscheidet
   * Bereiche wie „3 - 5“ bleiben eine Nummer („3-5“, bestehende Regel).
   */
  function reconcileStreet(streetValue, numberValue) {
    var split = splitStreet(streetValue);
    if (!split) return null;
    var number = asText(numberValue) || '';
    if (!number) return { street: split.street, number: split.number };
    var compact = function (v) {
      return String(v).replace(/\s+/g, '').toLowerCase();
    };
    if (compact(number) === compact(split.number)) return { street: split.street, number: number };
    if (compact(number) === compact(asText(streetValue) || '')) return { street: split.street, number: split.number };
    return null;
  }

  /**
   * Kam ein `input` von Autofill, Einfuegen oder Skript statt vom Tippen?
   * Nur dann darf der Abgleich sofort laufen; beim Tippen von „Hauptstraße 1“
   * wuerde er sonst mitten im Wort trennen. Autofill feuert `input` ohne
   * `inputType` (Safari) bzw. mit `insertReplacementText` (Chrome) und oft
   * fuer ein Feld, das gerade nicht den Fokus hat.
   */
  function isAutofillInput(event, activeElement) {
    if (!event) return false;
    if (event.type !== 'input') return true;
    var kind = event.inputType;
    if (!kind) return true;
    if (kind === 'insertReplacementText' || kind === 'insertFromPaste' || kind === 'insertFromDrop') return true;
    return !!event.target && activeElement !== event.target;
  }

  /**
   * Pruefung eines Felds (D.5). Ergebnis: Fehlermeldung (string) oder null.
   * `values` sind alle Formularwerte (Land steuert UID und PLZ),
   * `options` = { allowNoVat, consentRequired }.
   */
  function validateField(name, values, options) {
    var v = values && typeof values === 'object' ? values : {};
    var opts = options || {};
    var country = upperCountry(v.countryCode);
    var noVat = !!opts.allowNoVat && isTrue(v[NO_VAT_FIELD]) && isEuCountry(country);
    switch (name) {
      case 'countryCode':
        return country ? null : MSG.countryMissing;
      case 'company':
        if (length(v.company) < LIMITS.company[0]) return MSG.companyMissing;
        return length(v.company) > LIMITS.company[1] ? MSG.companyTooLong : null;
      case 'vatId':
        if (noVat) return null;
        return validateVatId(v.vatId, country, opts);
      case 'registerId': {
        if (!noVat) return null;
        var reg = asText(v.registerId) || '';
        if (!reg) return MSG.registerIdMissing;
        return REGISTER_ID_PATTERN.test(reg) ? null : MSG.registerIdFormat;
      }
      case 'firstName':
        return textRule(v.firstName, LIMITS.firstName, MSG.firstNameMissing, MSG.firstNameTooLong);
      case 'lastName':
        return textRule(v.lastName, LIMITS.lastName, MSG.lastNameMissing, MSG.lastNameTooLong);
      case 'email': {
        var email = asText(v.email) || '';
        if (email.length > LIMITS.email[1]) return MSG.emailTooLong;
        return EMAIL_PATTERN.test(email) ? null : MSG.emailInvalid;
      }
      case 'phone': {
        var phone = asText(v.phone);
        if (!phone) return null;
        return PHONE_PATTERN.test(phone) ? null : MSG.phoneInvalid;
      }
      case 'streetName':
        return textRule(v.streetName, LIMITS.streetName, MSG.streetNameMissing, MSG.streetNameTooLong);
      case 'houseNumber': {
        var number = asText(v.houseNumber) || '';
        if (!number) return MSG.houseNumberMissing;
        if (number.length > LIMITS.houseNumber[1]) return MSG.houseNumberTooLong;
        return /\d/.test(number) ? null : MSG.houseNumberInvalid;
      }
      case 'address2':
        return length(v.address2) > LIMITS.address2[1] ? MSG.address2TooLong : null;
      case 'zip':
        return validateZip(v.zip, country);
      case 'city':
        return textRule(v.city, LIMITS.city, MSG.cityMissing, MSG.cityTooLong);
      case 'note':
        return length(v.note) > LIMITS.note[1] ? MSG.noteTooLong : null;
      case 'consent':
        return opts.consentRequired && !isTrue(v.consent) ? MSG.consent : null;
      default:
        return null;
    }
  }

  /** Pflichttext mit Mindest- und Hoechstlaenge: zu kurz = fehlt, zu lang = eigene Meldung. */
  function textRule(value, limits, missing, tooLong) {
    var n = length(value);
    if (n < limits[0]) return missing;
    return n > limits[1] ? tooLong : null;
  }

  /** Browser-Pruefung vor dem Absenden: alle Feldfehler, leer = alles gut. */
  function validateBeforeSubmit(values, options) {
    var errors = {};
    for (var i = 0; i < VISIBLE_FIELDS.length; i++) {
      var message = validateField(VISIBLE_FIELDS[i], values, options);
      if (message) errors[VISIBLE_FIELDS[i]] = message;
    }
    return errors;
  }

  /** Banner-Text fuer n Fehler (D.4). */
  function errorSummary(count) {
    if (!count || count < 1) return '';
    return count === 1 ? MSG.summaryOne : fillMessage(MSG.summaryMany, { n: count });
  }

  function countKeys(object) {
    var n = 0;
    for (var key in object) if (Object.prototype.hasOwnProperty.call(object, key)) n++;
    return n;
  }

  /**
   * Beschriftung des UID-Felds an das gewaehlte Land anpassen (BA-23).
   * Rueckgabe: gesetzter Text oder null.
   */
  function updateVatLabel(form) {
    if (!form || typeof form.querySelector !== 'function') return null;
    var field = form.querySelector('[name="vatId"]');
    var country = form.querySelector('[name="countryCode"]');
    if (!field || !field.id) return null;
    var label = form.querySelector('label[for="' + field.id + '"]');
    if (!label) return null;
    var text = vatLabelFor(country ? country.value : '');
    label.textContent = text;
    return text;
  }

  // ───────────────────────────────────────────────────── Reine Funktionen

  function isTrue(value) {
    return value === true || value === 'on' || value === 'true' || value === '1';
  }

  /**
   * Empfehlungscode aus einem Query-String lesen (`?ref=partner-01`).
   * Akzeptiert den vollen `location.search` mit oder ohne fuehrendes `?`.
   */
  function parseRef(search) {
    if (typeof search !== 'string' || search.length === 0) return null;
    var query = search.charAt(0) === '?' ? search.slice(1) : search;
    var parts = query.split('&');
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].slice(0, 4) !== 'ref=') continue;
      var value;
      try {
        value = decodeURIComponent(parts[i].slice(4).replace(/\+/g, ' ')).trim();
      } catch (e) {
        return null;
      }
      return REF_PATTERN.test(value) ? value : null;
    }
    return null;
  }

  /** Einen Cookie-Wert aus `document.cookie` lesen. */
  function readCookie(cookieString, name) {
    if (typeof cookieString !== 'string' || cookieString.length === 0) return null;
    var parts = cookieString.split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].trim();
      var eq = part.indexOf('=');
      if (eq < 0) continue;
      if (part.slice(0, eq) !== name) continue;
      try {
        return asText(decodeURIComponent(part.slice(eq + 1)));
      } catch (e) {
        return asText(part.slice(eq + 1));
      }
    }
    return null;
  }

  /**
   * Empfehlungscode fuer das versteckte Feld: erst der Link der aktuellen
   * Seite, dann der Cookie aus snippets/referral-capture.liquid. Unplausible
   * Werte werden verworfen, statt beim Server einen Validierungsfehler zu
   * erzeugen (der Code ist ein Nebenschauplatz, der Antrag ist die Hauptsache).
   */
  function resolveRef(search, cookieString) {
    var fromQuery = parseRef(search);
    if (fromQuery) return fromQuery;
    var fromCookie = readCookie(cookieString, REF_COOKIE);
    return fromCookie && REF_PATTERN.test(fromCookie) ? fromCookie : null;
  }

  function joinText(a, b) {
    return [asText(a) || '', asText(b) || ''].join(' ').trim();
  }

  /**
   * Request-Body aus den Formularwerten.
   *
   * Leere Textfelder gehen als leerer String mit: der Server macht daraus
   * `null` und meldet Pflichtfelder selbst. Der Honeypot ist IMMER dabei,
   * auch leer — fehlt er, prueft der Server ihn nicht. Ein blosses
   * UID-Praefix („ATU“) geht leer raus. Bei „keine UID“ geht keine UID mit,
   * sonst keine Registernummer.
   */
  function buildPayload(values) {
    var source = values && typeof values === 'object' ? values : {};
    var body = {};

    for (var i = 0; i < FIELDS.length; i++) {
      var key = FIELDS[i];
      body[key] = asText(source[key]) || '';
    }

    var noVat = isTrue(source[NO_VAT_FIELD]) && isEuCountry(body.countryCode);
    if (isBareVatPrefix(body.vatId) || noVat) body.vatId = '';
    if (!noVat) body.registerId = '';
    body[NO_VAT_FIELD] = noVat;

    body.contactName = joinText(body.firstName, body.lastName);
    body.street = joinText(body.streetName, body.houseNumber);

    body[CONSENT_FIELD] = isTrue(source[CONSENT_FIELD]);

    var honeypot = source[HONEYPOT_FIELD];
    body[HONEYPOT_FIELD] = typeof honeypot === 'string' ? honeypot : '';

    var started = Number(source[STARTED_FIELD]);
    body[STARTED_FIELD] = isFinite(started) && started > 0 ? started : null;

    // Anfrage-Kennung nur in gueltiger Form, sonst gar nicht (alter Server und
    // altes SDK sehen denselben Body wie bisher).
    var leadId = asText(source.leadId);
    if (leadId && LEAD_ID_PATTERN.test(leadId)) body.leadId = leadId;
    var leadRef = asText(source.leadRef);
    if (leadRef && LEAD_REF_PATTERN.test(leadRef)) body.leadRef = leadRef;

    return body;
  }

  /**
   * Feldfehler des Servers auf die eigenen Felder eingrenzen. Ein unbekannter
   * Schluessel wuerde sonst im Nirgendwo landen und der Kunde saehe gar nichts.
   * Fehler zu den kombinierten v1-Feldern landen am sichtbaren v2-Feld.
   */
  function normalizeErrors(errors) {
    var source = errors && typeof errors === 'object' ? errors : {};
    var out = {};
    var allowed = PAYLOAD_KEYS.concat([FORM_ERROR_KEY]);
    for (var i = 0; i < allowed.length; i++) {
      var key = allowed[i];
      var message = asText(source[key]);
      if (!message) continue;
      var target = Object.prototype.hasOwnProperty.call(ERROR_ALIASES, key) ? ERROR_ALIASES[key] : key;
      if (!out[target]) out[target] = message;
    }
    return out;
  }

  /** Fehlermeldung aus einer Serverantwort, immer in Sie-Form. */
  function messageOf(payload) {
    var message = payload && typeof payload === 'object' ? asText(payload.message) : null;
    return message || COPY.fallback;
  }

  /**
   * Handlungs-Link zum Banner je Server-`code`, oder null.
   *
   *   duplicate (409) → Anmeldeseite („Jetzt anmelden“)
   *
   * Ergebnis { href, label, kind }. `context.loginUrl` kommt aus
   * `data-b2b-login-url`; die URL landet in einem `href`, deshalb nur Pfade
   * im eigenen Shop.
   */
  function actionLinkFor(payload, context) {
    var code = payload && typeof payload === 'object' ? asText(payload.code) : null;
    if (!code) return null;
    var normalized = code.toLowerCase();
    for (var i = 0; i < LOGIN_CODES.length; i++) {
      if (LOGIN_CODES[i] === normalized) {
        var c = context && typeof context === 'object' ? context : {};
        return { href: ownPath(c.loginUrl, DEFAULT_LOGIN_URL), label: COPY.login, kind: 'login' };
      }
    }
    return null;
  }

  /**
   * Antwort des Servers in ein Ergebnis fuer die Anzeige uebersetzen.
   *
   *   { kind: 'success', review?: string, id?: string }
   *   { kind: 'validation', errors: {...}, message: string|null }
   *   { kind: 'banner', message: string, action?: {href,label,kind} }
   *
   * Erfolg gilt nur bei `ok:true` im Body. Ein 2xx ohne verwertbaren Body
   * kommt nicht vom Endpoint (Proxy-Fehlerseite, HTML-Login) und wird deshalb
   * bewusst NICHT als Erfolg gewertet. `id` steht nur beim echten Antrag
   * (201); der stille Honeypot-Erfolg (200 ohne id) meldet kein Analytics-Event.
   */
  function interpretResponse(status, payload, context) {
    var body = payload && typeof payload === 'object' ? payload : null;

    if (body && body.ok === true) {
      var success = { kind: 'success' };
      var review = asText(body.review);
      if (review) success.review = review;
      var id = asText(body.id);
      if (id) success.id = id;
      return success;
    }

    if (status === 400 && body && body.code === 'validation') {
      var errors = normalizeErrors(body.errors);
      var formMessage = errors[FORM_ERROR_KEY] || null;
      delete errors[FORM_ERROR_KEY];
      return { kind: 'validation', errors: errors, message: formMessage };
    }

    var outcome = { kind: 'banner', message: messageOf(body) };
    var action = actionLinkFor(body, context);
    if (action) outcome.action = action;
    return outcome;
  }

  /**
   * Welche Erfolgsanzeige (D.7)? Die Server-Antwort entscheidet, nie der
   * Seitentext (Risiko R6):
   *   'approved' → v2b „Sie sind freigeschaltet“ (nur wenn die Sektion den
   *                Platz dafuer rendert, Schalter auto_freischaltung_text)
   *   sonst      → v2a „Vielen Dank, Ihr Antrag ist da.“
   * 'checking' faellt bis A2 (Polling auf den Status-Endpoint) auf v2a zurueck.
   */
  function successVariant(review, hasAutoSlot) {
    if (hasAutoSlot && review === 'approved') return 'approved';
    return 'v2a';
  }

  /**
   * Anfrage bei denk-collect anlegen (window.denk.lead.begin, E3: innerhalb von
   * 30 min derselbe Vorgang, auch nach einem abgelehnten Absenden). Ergebnis
   * { leadId, ref } oder null (altes SDK, Overlay, noch nicht initialisiert).
   */
  function beginLead(win) {
    try {
      var w = win || global;
      var api = w && w.__denkReady && w.denk && w.denk.lead;
      if (!api || typeof api.begin !== 'function') return null;
      var lead = api.begin({ form: 'b2b_register', placement: 'b2b_page' });
      if (!lead || !asText(lead.leadId)) return null;
      return { leadId: asText(lead.leadId), ref: asText(lead.ref) };
    } catch (e) {
      return null;
    }
  }

  /**
   * Analytics-Event zum erfolgreichen Antrag, oder null. Nur bei `outcome.id`
   * (echter Antrag, 201): der Honeypot bekommt 200 ohne id und darf nichts
   * zaehlen. `answers` traegt nur Land (klein) und ob eine UID angegeben war.
   */
  function applicationEvent(outcome, values) {
    if (!outcome || outcome.kind !== 'success' || !asText(outcome.id)) return null;
    var body = buildPayload(values);
    var props = {
      form: 'b2b_register',
      placement: 'b2b_page',
      answers: {
        country: body.countryCode ? body.countryCode.toLowerCase() : '',
        vat: !!body.vatId
      }
    };
    if (body.leadId) props.leadId = body.leadId;
    if (body.leadRef) props.ref = body.leadRef;
    return ['b2b_application_submitted', props];
  }

  /**
   * Anfrage senden. `options.fetch` und `options.endpoint` existieren fuer die
   * Tests; im Browser kommen beide aus der Umgebung bzw. vom Formular.
   */
  function requestRegistration(payload, options) {
    var opts = options && typeof options === 'object' ? options : {};
    var fetchImpl = typeof opts.fetch === 'function' ? opts.fetch : global && global.fetch;
    var target = asText(opts.endpoint) || ENDPOINT;

    if (typeof fetchImpl !== 'function') {
      return Promise.resolve({ kind: 'banner', message: COPY.network });
    }

    return fetchImpl(target, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response
          .json()
          .catch(function () {
            return null;
          })
          .then(function (body) {
            return interpretResponse(response.status, body, opts.context);
          });
      })
      .catch(function () {
        return { kind: 'banner', message: COPY.network };
      });
  }

  /**
   * Formularwerte pruefen und nur bei bestandener Browser-Pruefung senden.
   * Bei einem Verstoss geht KEIN Request raus; das Ergebnis hat dieselbe Form
   * wie ein 400 `validation` des Servers (plus `client: true`, Banner-Text
   * mit Zaehlung).
   */
  function submitRegistration(values, options) {
    var opts = options && typeof options === 'object' ? options : {};
    var errors = validateBeforeSubmit(values, opts.rules);
    var count = countKeys(errors);
    if (count > 0) {
      return Promise.resolve({ kind: 'validation', errors: errors, message: errorSummary(count), client: true });
    }
    return requestRegistration(buildPayload(values), opts);
  }

  /**
   * FO-33 (BA-22): Der Absendeknopf kommt aus Liquid gesperrt (`disabled`),
   * weil das Formular ohne JavaScript an `#` posten wuerde. Frei wird er erst,
   * NACHDEM setup() den Submit-Listener registriert hat.
   * Gibt true zurueck, wenn ein Knopf freigegeben wurde.
   */
  function releaseSubmit(form) {
    if (!form || typeof form.querySelector !== 'function') return false;
    var button = form.querySelector('button[type="submit"], input[type="submit"]');
    if (!button) return false;
    if (button.getAttribute && button.getAttribute('aria-busy') === 'true') return false;
    button.disabled = false;
    return true;
  }

  // ───────────────────────────────────────────────────────── DOM und Ablauf

  /**
   * BA-59: abgelehnter oder gesperrter Haendler. Die Formular-Karte ist
   * verborgen (`hidden`), der Knopf „Erneut beantragen“ auf der
   * Abgelehnt-Karte zeigt sie, blendet seine Zeile aus, setzt den
   * Zeitstempel neu (das Lesen der Karte zaehlt nicht zum Ausfuellen) und
   * springt zum ersten Feld. Ohne verborgene Karte (Erstantrag) passiert
   * nichts. Gibt die Zahl der verbundenen Knoepfe zurueck (Test).
   */
  function bindReapply(form, root, started) {
    var card = form && form.closest ? form.closest('[data-b2b-register-card]') : null;
    if (!card || !card.id || !root) return 0;
    var buttons = root.querySelectorAll('[data-b2b-reapply][aria-controls="' + card.id + '"]');
    for (var r = 0; r < buttons.length; r++) {
      buttons[r].addEventListener('click', function (event) {
        var button = event.currentTarget;
        card.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        var row = button.closest ? button.closest('[data-b2b-reapply-row]') : null;
        if (row) row.hidden = true;
        if (started) started.value = String(Date.now());
        var first = form.querySelector('[name="countryCode"]');
        try {
          card.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) {
          card.scrollIntoView();
        }
        if (first) {
          try {
            first.focus({ preventScroll: true });
          } catch (e) {
            first.focus();
          }
        }
      });
    }
    return buttons.length;
  }

  var api = {
    ENDPOINT: ENDPOINT,
    DEFAULT_LOGIN_URL: DEFAULT_LOGIN_URL,
    LOGIN_CODES: LOGIN_CODES,
    FIELDS: FIELDS,
    LEGACY_FIELDS: LEGACY_FIELDS,
    ERROR_ALIASES: ERROR_ALIASES,
    VISIBLE_FIELDS: VISIBLE_FIELDS,
    PAYLOAD_KEYS: PAYLOAD_KEYS,
    CHECKBOX_FIELDS: CHECKBOX_FIELDS,
    NO_VAT_FIELD: NO_VAT_FIELD,
    CONSENT_FIELD: CONSENT_FIELD,
    HONEYPOT_FIELD: HONEYPOT_FIELD,
    STARTED_FIELD: STARTED_FIELD,
    FORM_ERROR_KEY: FORM_ERROR_KEY,
    REF_PATTERN: REF_PATTERN,
    REF_COOKIE: REF_COOKIE,
    EMAIL_PATTERN: EMAIL_PATTERN,
    PHONE_PATTERN: PHONE_PATTERN,
    REGISTER_ID_PATTERN: REGISTER_ID_PATTERN,
    LIMITS: LIMITS,
    COPY: COPY,
    MSG: MSG,
    COUNTRY_NAMES: COUNTRY_NAMES,
    COUNTRY_ARTICLES: COUNTRY_ARTICLES,
    EU_VAT_PREFIXES: EU_VAT_PREFIXES,
    DEFAULT_CONTACT: DEFAULT_CONTACT,
    readContact: readContact,
    ownPath: ownPath,
    actionLinkFor: actionLinkFor,
    contactSentence: contactSentence,
    buildCopy: buildCopy,
    fillMessage: fillMessage,
    countryName: countryName,
    countryAkk: countryAkk,
    countryDat: countryDat,
    vatExampleFor: vatExampleFor,
    vatFormatMessage: vatFormatMessage,
    parseRef: parseRef,
    readCookie: readCookie,
    resolveRef: resolveRef,
    buildPayload: buildPayload,
    normalizeErrors: normalizeErrors,
    messageOf: messageOf,
    interpretResponse: interpretResponse,
    successVariant: successVariant,
    beginLead: beginLead,
    applicationEvent: applicationEvent,
    LEAD_FIELDS: LEAD_FIELDS,
    requestRegistration: requestRegistration,
    VAT_PATTERNS: VAT_PATTERNS,
    VAT_EXAMPLES: VAT_EXAMPLES,
    EXPORT_VAT_EXAMPLE: EXPORT_VAT_EXAMPLE,
    EU_ISO_COUNTRIES: EU_ISO_COUNTRIES,
    VAT_CHECKED_COUNTRIES: VAT_CHECKED_COUNTRIES,
    REGISTRATION_EXCLUDED: REGISTRATION_EXCLUDED,
    EXPORT_COUNTRIES: EXPORT_COUNTRIES,
    EXPORT_VAT_ID_PATTERN: EXPORT_VAT_ID_PATTERN,
    VAT_LABELS: VAT_LABELS,
    normalizeVatId: normalizeVatId,
    normalizeExportVatId: normalizeExportVatId,
    vatLabelFor: vatLabelFor,
    updateVatLabel: updateVatLabel,
    parseVatId: parseVatId,
    vatPrefixToIsoCountry: vatPrefixToIsoCountry,
    isVatFormatValid: isVatFormatValid,
    vatPrefixFor: vatPrefixFor,
    isBareVatPrefix: isBareVatPrefix,
    nextVatValue: nextVatValue,
    vatPlaceholderFor: vatPlaceholderFor,
    vatHintFor: vatHintFor,
    zipInputModeFor: zipInputModeFor,
    zipPlaceholderFor: zipPlaceholderFor,
    checkVatChecksum: checkVatChecksum,
    validateVatId: validateVatId,
    vatNote: vatNote,
    validateZip: validateZip,
    splitStreet: splitStreet,
    reconcileStreet: reconcileStreet,
    isAutofillInput: isAutofillInput,
    validateField: validateField,
    validateBeforeSubmit: validateBeforeSubmit,
    errorSummary: errorSummary,
    submitRegistration: submitRegistration,
    releaseSubmit: releaseSubmit,
    bindReapply: bindReapply
  };

  if (global) global.B2BRegister = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;

  // Ohne DOM (Node-Test) endet das Skript hier.
  if (!doc || !global || typeof global.fetch !== 'function') return;

  function fieldElement(form, name) {
    return form.querySelector('[name="' + name + '"]');
  }

  function errorElement(form, name) {
    return form.querySelector('[data-error-for="' + name + '"]');
  }

  function rulesOf(form) {
    return {
      allowNoVat: form.getAttribute('data-allow-no-vat') === 'true',
      consentRequired: form.getAttribute('data-consent-required') === 'true'
    };
  }

  function readValues(form) {
    var values = {};
    for (var i = 0; i < PAYLOAD_KEYS.length; i++) {
      var key = PAYLOAD_KEYS[i];
      var field = fieldElement(form, key);
      if (!field) continue;
      values[key] = field.type === 'checkbox' ? field.checked : field.value;
    }
    return values;
  }

  /** Eingeklappte Bereiche (Adresszusatz, Nachricht) oeffnen, wenn ein Feld darin gebraucht wird. */
  function revealField(field) {
    var details = field && field.closest ? field.closest('details') : null;
    if (details && !details.open) details.open = true;
  }

  function setFieldError(form, name, message) {
    var box = errorElement(form, name);
    var field = fieldElement(form, name);
    if (box) {
      box.textContent = message || '';
      box.hidden = !message;
    }
    if (field) {
      if (message) field.setAttribute('aria-invalid', 'true');
      else field.removeAttribute('aria-invalid');
    }
  }

  function clearAllErrors(form) {
    for (var i = 0; i < PAYLOAD_KEYS.length; i++) setFieldError(form, PAYLOAD_KEYS[i], null);
    hideBanner(form);
  }

  /** Fehler zeigen, Fokus auf das erste Feld in Formular-Reihenfolge (D.4). */
  function showFieldErrors(form, errors) {
    var first = null;
    for (var i = 0; i < VISIBLE_FIELDS.length; i++) {
      var key = VISIBLE_FIELDS[i];
      if (!Object.prototype.hasOwnProperty.call(errors, key)) continue;
      setFieldError(form, key, errors[key]);
      var field = fieldElement(form, key);
      if (field && !first) first = field;
    }
    if (first) {
      revealField(first);
      try {
        first.focus({ preventScroll: false });
      } catch (e) {
        first.focus();
      }
    }
  }

  /** Hinweis unter dem UID-Feld (Pruefziffer-Warnung oder „Format in Ordnung“). */
  function showVatNote(form) {
    var box = form.querySelector('[data-note-for="vatId"]');
    if (!box) return;
    var values = readValues(form);
    var note = rulesOf(form).allowNoVat && isTrue(values[NO_VAT_FIELD]) ? null : vatNote(values.vatId, values.countryCode);
    box.textContent = note ? note.message : '';
    box.hidden = !note;
    box.setAttribute('data-kind', note ? note.kind : '');
  }

  function bannerElement(form) {
    return form.querySelector('[data-b2b-register-banner]');
  }

  /**
   * Der Banner ist wie snippets/banner.liquid aufgebaut: Icon und Text stehen
   * im selben Raster. Der Text gehoert deshalb in den Span, nicht in den
   * Container — sonst loescht die Meldung das Icon.
   */
  function bannerMessageElement(form) {
    var box = bannerElement(form);
    if (!box) return null;
    return box.querySelector('[data-b2b-register-banner-message]') || box;
  }

  /** Handlungs-Link aus einer frueheren Meldung entfernen. */
  function removeBannerAction(box) {
    var links = box.querySelectorAll('.b2b-register__banner-action');
    for (var i = 0; i < links.length; i++) {
      if (links[i].parentElement) links[i].parentElement.removeChild(links[i]);
    }
  }

  /**
   * Banner fuellen. Die Meldung geht ueber textContent; der Handlungs-Link
   * (actionLinkFor) wird als echtes <a> hinter dem Text angehaengt.
   * `scroll`: bei Feldfehlern bleibt der Blick beim Feld (Fokus), sonst
   * rollt die Seite zum Banner.
   */
  function showBanner(form, message, action, scroll) {
    var box = bannerElement(form);
    if (!box) return;
    removeBannerAction(box);
    bannerMessageElement(form).textContent = message;
    if (action && action.href) {
      var link = doc.createElement('a');
      link.className = 'b2b-register__banner-action link';
      link.setAttribute('href', action.href);
      link.textContent = action.label;
      box.appendChild(link);
    }
    box.hidden = false;
    if (scroll !== false) {
      try {
        box.scrollIntoView({ block: 'nearest' });
      } catch (e) {}
    }
  }

  function hideBanner(form) {
    var box = bannerElement(form);
    if (!box) return;
    bannerMessageElement(form).textContent = '';
    removeBannerAction(box);
    box.hidden = true;
  }

  function submitButton(form) {
    return form.querySelector('button[type="submit"], input[type="submit"]');
  }

  /**
   * Ladezustand: Knopf gesperrt, sichtbarer Text „Antrag wird gesendet …“
   * (Span data-b2b-submit-loading), aria-busy fuer Screenreader und Tests.
   * Der Knopf ist bewusst KEIN custom-button des Themes: der setzt bei jedem
   * submit-Ereignis aria-busy und blieb nach einer Browser-Pruefung mit
   * Fehlern in der Ladeanimation haengen.
   */
  function setLoading(form, on) {
    var button = submitButton(form);
    if (!button) return;
    var label = button.querySelector('[data-b2b-submit-label]');
    var loading = button.querySelector('[data-b2b-submit-loading]');
    if (on) {
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      if (label) label.hidden = true;
      if (loading) loading.hidden = false;
      return;
    }
    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (label) label.hidden = false;
    if (loading) loading.hidden = true;
  }

  /** Erfolgsanzeige (D.7) zeigen, Formular verschwinden lassen. */
  function showSuccess(form, outcome, values) {
    var scope = form.closest('[data-b2b-register-card]') || form.parentElement || doc;
    var autoSlot = scope.querySelector('[data-b2b-register-success-auto="approved"]');
    var variant = successVariant(outcome && outcome.review, !!autoSlot);
    var box = variant === 'approved' ? autoSlot : scope.querySelector('[data-b2b-register-success]');

    var emails = scope.querySelectorAll('[data-b2b-success-email]');
    for (var i = 0; i < emails.length; i++) emails[i].textContent = asText(values && values.email) || COPY.emailFallback;
    var companies = scope.querySelectorAll('[data-b2b-success-company]');
    for (var j = 0; j < companies.length; j++) companies[j].textContent = asText(values && values.company) || '';

    form.hidden = true;
    var intro = scope.querySelector('[data-b2b-register-intro]');
    if (intro) intro.hidden = true;
    if (box) {
      box.hidden = false;
      try {
        box.scrollIntoView({ block: 'nearest' });
        // Der Fokus muss mitwandern, sonst steht er auf dem versteckten Formular.
        box.focus({ preventScroll: true });
      } catch (e) {}
    }

    // denk-collect: Haendler-Antrag ist kein Verkaufs-Lead (E13), eigenes
    // Event statt generate_lead; nur beim echten Antrag (outcome.id).
    try {
      var item = applicationEvent(outcome, values);
      if (item) {
        global.__denkQueue = global.__denkQueue || [];
        global.__denkQueue.push(item);
      }
    } catch (e) {}
  }

  /** UID-Feld an das Land anpassen: Praefix, Platzhalter, Hilfe, Beschriftung (D.3). */
  function applyCountry(form, initial) {
    var country = fieldElement(form, 'countryCode');
    var code = country ? country.value : '';
    var vat = fieldElement(form, 'vatId');
    if (vat) {
      var next = nextVatValue(vat.value, code);
      if (next !== vat.value) vat.value = next;
      vat.setAttribute('placeholder', vatPlaceholderFor(code));
      // CH/LI: UID freiwillig (BA-23), sonst Pflicht.
      var pflicht = !isExportCountry(code);
      vat.required = pflicht;
      if (pflicht) vat.setAttribute('aria-required', 'true');
      else vat.removeAttribute('aria-required');
    }
    var hint = form.querySelector('[data-hint-for="vatId"]');
    if (hint) hint.textContent = vatHintFor(code);
    updateVatLabel(form);

    var noVatBox = form.querySelector('[data-b2b-novat]');
    if (noVatBox) noVatBox.hidden = !isEuCountry(code);
    applyNoVat(form);

    var zip = fieldElement(form, 'zip');
    if (zip) {
      zip.setAttribute('inputmode', zipInputModeFor(code));
      zip.setAttribute('placeholder', zipPlaceholderFor(code));
    }
    if (!initial) showVatNote(form);
  }

  /** „Ich habe keine UID-Nummer“: UID-Feld aus, Registernummer an (D.2 Nr. 3a/3b). */
  function applyNoVat(form) {
    var box = fieldElement(form, NO_VAT_FIELD);
    var country = fieldElement(form, 'countryCode');
    var on = !!(box && box.checked && isEuCountry(country ? country.value : ''));
    var vatWrap = form.querySelector('[data-field="vatId"]');
    var regWrap = form.querySelector('[data-field="registerId"]');
    if (vatWrap) vatWrap.hidden = on;
    if (regWrap) regWrap.hidden = !on;
    var reg = fieldElement(form, 'registerId');
    if (reg) {
      if (on) reg.setAttribute('required', '');
      else reg.removeAttribute('required');
    }
    if (on) setFieldError(form, 'vatId', null);
    else setFieldError(form, 'registerId', null);
  }

  /** Pruefung eines Felds beim Verlassen (D.4); leere, nie beruehrte Felder bleiben ruhig. */
  function checkOnBlur(form, name) {
    if (!contains(VISIBLE_FIELDS, name)) return;
    var values = readValues(form);
    var field = fieldElement(form, name);
    var touched = field && field.getAttribute('data-touched') === 'true';
    var message = validateField(name, values, rulesOf(form));
    var empty = !asText(values[name]) || (name === 'vatId' && isBareVatPrefix(values[name]));
    if (!touched && empty) message = null;
    setFieldError(form, name, message);
    if (name === 'vatId' || name === 'countryCode') showVatNote(form);
  }

  function setup(form) {
    if (form.getAttribute('data-b2b-register-ready') === 'true') return;
    form.setAttribute('data-b2b-register-ready', 'true');
    // Eigene Pruefung mit den Texten aus D.5 statt Browser-Blasen.
    form.noValidate = true;

    // Zeitfenster des Servers: der Stempel muss aus dem Browser kommen, eine
    // in Liquid gerenderte Zeit waere im Full-Page-Cache eingefroren.
    var started = form.querySelector('[data-b2b-register-started]');
    if (started) started.value = String(Date.now());

    var refField = form.querySelector('[data-b2b-register-ref]');
    if (refField) {
      var ref = resolveRef(global.location ? global.location.search : '', doc.cookie);
      refField.value = ref || '';
    }

    var busy = false;

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (busy) return;
      busy = true;

      clearAllErrors(form);
      // BA-54: Netz fuer Autofill ohne jedes Ereignis (aeltere iOS-Versionen).
      applyStreetReconcile(form);

      var values = readValues(form);
      var rules = rulesOf(form);
      var clientErrors = validateBeforeSubmit(values, rules);
      var count = countKeys(clientErrors);
      if (count > 0) {
        busy = false;
        showBanner(form, errorSummary(count), null, false);
        showFieldErrors(form, clientErrors);
        return;
      }

      setLoading(form, true);

      // Anfrage-Kennung (E2): neben dem Partner-Code `ref`, nie an seiner Stelle.
      var lead = beginLead(global);
      if (lead) {
        values.leadId = lead.leadId;
        values.leadRef = lead.ref;
      }

      submitRegistration(values, {
        endpoint: form.getAttribute('data-endpoint'),
        context: { loginUrl: form.getAttribute('data-b2b-login-url') },
        rules: rules
      }).then(function (outcome) {
        if (outcome.kind === 'success') {
          // Ladezustand bleibt stehen, das Formular verschwindet ohnehin.
          showSuccess(form, outcome, values);
          return;
        }

        busy = false;
        setLoading(form, false);

        if (outcome.kind === 'validation') {
          var n = countKeys(outcome.errors);
          var text = outcome.message || errorSummary(n);
          if (text) showBanner(form, text, null, n === 0);
          showFieldErrors(form, outcome.errors);
          return;
        }

        showBanner(form, outcome.message, outcome.action);
      });
    });

    // Sobald der Kunde ein Feld anfasst, verschwindet dessen Fehlermeldung.
    form.addEventListener('input', function (event) {
      var target = event.target;
      if (!target || !target.name) return;
      target.setAttribute('data-touched', 'true');
      if (target.getAttribute('aria-invalid') === 'true') setFieldError(form, target.name, null);
      // BA-54: Autofill fuellt Strasse und Hausnummer ohne blur.
      if ((target.name === 'streetName' || target.name === 'houseNumber') && isAutofillInput(event, doc.activeElement)) {
        applyStreetReconcile(form);
      }
    });

    // D.4: Pruefung beim Verlassen eines Felds, nie je Tastendruck.
    form.addEventListener(
      'blur',
      function (event) {
        var target = event.target;
        if (!target || !target.name) return;
        if (target.name === 'vatId' && target.value) {
          var land = fieldElement(form, 'countryCode');
          if (!isExportCountry(land ? land.value : '')) target.value = normalizeVatId(target.value);
        }
        checkOnBlur(form, target.name);
      },
      true
    );

    form.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.name) return;
      target.setAttribute('data-touched', 'true');
      if (target.name === 'countryCode') {
        applyCountry(form, false);
        // Steht schon eine PLZ da, gleich gegen das neue Land pruefen.
        if (asText(fieldElement(form, 'zip') && fieldElement(form, 'zip').value)) checkOnBlur(form, 'zip');
      }
      if (target.name === NO_VAT_FIELD) {
        applyNoVat(form);
        showVatNote(form);
      }
      if (target.name === 'streetName' || target.name === 'houseNumber') {
        // D.4 Autofill-Split, seit BA-54 mit Abgleich (reconcileStreet).
        applyStreetReconcile(form);
      }
      if (target.type === 'checkbox' || target.tagName === 'SELECT') checkOnBlur(form, target.name);
    });

    // Sprung-Knopf im Hero: zum Formular, Fokus auf das erste Feld (LP-09).
    var jumps = doc.querySelectorAll('[data-b2b-jump]');
    for (var j = 0; j < jumps.length; j++) {
      jumps[j].addEventListener('click', function (event) {
        var first = fieldElement(form, 'countryCode');
        var hiddenCard = form.closest ? form.closest('[data-b2b-register-card][hidden]') : null;
        if (!first || form.hidden || hiddenCard) return;
        event.preventDefault();
        var anchor = doc.getElementById('formular') || form;
        try {
          anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (e) {
          anchor.scrollIntoView();
        }
        try {
          first.focus({ preventScroll: true });
        } catch (e) {
          first.focus();
        }
        if (global.history && global.history.replaceState) {
          try {
            global.history.replaceState(null, '', '#formular');
          } catch (e) {}
        }
      });
    }

    bindReapply(form, doc, started);

    applyCountry(form, true);

    // FO-33 (BA-22): erst jetzt, mit registriertem Listener, darf der Knopf los.
    releaseSubmit(form);
  }

  /** Strasse/Hausnummer im Formular abgleichen (reconcileStreet). */
  function applyStreetReconcile(form) {
    var street = fieldElement(form, 'streetName');
    var number = fieldElement(form, 'houseNumber');
    if (!street || !number) return;
    var next = reconcileStreet(street.value, number.value);
    if (!next) return;
    if (street.value !== next.street) street.value = next.street;
    if (number.value !== next.number) number.value = next.number;
    setFieldError(form, 'houseNumber', null);
  }

  function init() {
    var forms = doc.querySelectorAll('form[data-b2b-register]');
    for (var i = 0; i < forms.length; i++) setup(forms[i]);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme-Editor: eine neu gerenderte Sektion bringt ein frisches Formular mit.
  doc.addEventListener('shopify:section:load', init);
})(typeof window !== 'undefined' ? window : globalThis, typeof document !== 'undefined' ? document : null);
